import { useCallback, useEffect, useRef } from "react";
import { useFetcher, useRevalidator } from "react-router";

/**
 * Fire-and-forget TPA marketing-consent capture (SFCC-11425).
 *
 * POSTs to `/resources/tpa-consent` (writes `c_tpaLearningCenterConsentData=true`
 * for the authenticated customer) and, on a successful write, revalidates the
 * root loader so the legacy `segmentTpaSuppressed` flag flips. That flag now
 * means "TPA consent capture still needed" rather than "suppress first-party
 * Segment".
 *
 * The TPA-user / registered gating lives at the call site (via
 * `useRootLoaderData()` — `segmentTpaSuppressed` / `isRegistered`); this hook
 * just performs the write + revalidate. The server action is the authority on
 * "registered" (a guest POST is a no-op), so callers don't need to be perfectly
 * in sync with the post-registration auth state.
 */
export function useSetTpaConsent(): () => void {
  const fetcher = useFetcher<{ success?: boolean }>();
  const { revalidate } = useRevalidator();
  const submitted = useRef(false);

  useEffect(() => {
    if (fetcher.state !== "idle") {
      submitted.current = true;
      return;
    }
    if (!submitted.current) return;
    submitted.current = false;
    // Only revalidate when the profile flag actually changed; a guest/no-op or a
    // failed write returns success:false and needs no revalidate.
    if (fetcher.data?.success) revalidate();
  }, [fetcher.state, fetcher.data, revalidate]);

  return useCallback(() => {
    if (fetcher.state !== "idle") return;
    fetcher.submit({}, { method: "post", action: "/resources/tpa-consent" });
  }, [fetcher]);
}
